import React, { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import "../admin-ui.css";

type CleanupHistoryRecord = {
  id: string;
  createdAt: string;
  performedBy: string;
  removedStudents: number;
  archiveName: string;
  archivePath?: string;
};

type LastBackup = {
  archiveName: string;
  archivePath: string;
  createdAt: string;
} | null;

type SessionStatus = {
  activeStudents: number;
  admins: number;
  superadmins: number;
  resultsCount: number;
  protocolsCount: number;
  questionBanksCount: number;
  taskBanksCount: number;
  lastBackup: LastBackup;
  cleanupHistory: CleanupHistoryRecord[];
};

type CleanupResult = {
  removedStudents: number;
  archiveName: string;
  archivePath: string;
};

type CleanupAnalysis = {
  summary?: {
    totalStudentUsers: number;
    activeStudents: number;
    studentsWithResults: number;
    assignedWithoutResults: number;
    inactiveWithoutResults: number;
    duplicateByFioBirthDateGroups: number;
    duplicateByUsernameGroups: number;
    studentsInMultipleBatchesGroups: number;
    importBatches: number;
    importHistoryRecords: number;
    usersWithoutImportId: number;
    lastImportWithoutImportId: number;
  };
  fields?: {
    users: string[];
    lastImportStudents: string[];
    results: string[];
  };
  batches?: Array<{
    importId: string;
    importDate: string;
    importFileName: string;
    specialty: string;
    assignmentType: "test" | "task";
    studentsCount: number;
    studentsWithoutResultsCount: number;
    studentsWithResultsCount: number;
  }>;
};

type ImportCleanupPreview = {
  importId: string;
  importFileName: string;
  specialty: string;
  assignmentType: "test" | "task";
  totalStudents: number;
  activeStudents: number;
  studentsWithResults: number;
  assignedWithoutResults: number;
  inactiveWithoutResults: number;
  duplicatesInOtherBatches: number;
  safeDeleteCount: number;
  students: Array<{
    fullName: string;
    birthDate: string;
    username: string;
    accessEnabled: boolean;
    hasResult: boolean;
    cleanupStatus: {
      key: string;
      label: string;
      canAutoDelete: boolean;
    };
  }>;
};

type ExamSession = {
  sessionId: string;
  name: string;
  specialty: string;
  assignmentType: "test" | "task";
  createdAt: string;
  studentsCount: number;
  completedCount: number;
  incompleteCount: number;
  students?: Array<{
    username: string;
    fullName?: string;
    specialty?: string;
    hasResult?: boolean;
  }>;
  results?: Array<{
    username: string;
    fullName?: string;
    score?: number | null;
    total?: number | null;
    date?: string | null;
    finishedAt?: string | null;
  }>;
};

const emptyStatus: SessionStatus = {
  activeStudents: 0,
  admins: 0,
  superadmins: 0,
  resultsCount: 0,
  protocolsCount: 0,
  questionBanksCount: 0,
  taskBanksCount: 0,
  lastBackup: null,
  cleanupHistory: [],
};

const MOSCOW_TIME_ZONE = "Europe/Moscow";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("ru-RU") : "?";

const formatTime = (value?: string) =>
  value
    ? new Date(value).toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "?";

const formatMoscowDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("ru-RU", {
        timeZone: MOSCOW_TIME_ZONE,
      })
    : formatDate(value);

const formatMoscowTime = (value?: string) =>
  value
    ? new Date(value).toLocaleTimeString("ru-RU", {
        timeZone: MOSCOW_TIME_ZONE,
        hour: "2-digit",
        minute: "2-digit",
      })
    : formatTime(value);

const preservedItems = [
  "результаты экзаменов",
  "протоколы",
  "базы тестов",
  "базы ситуационных задач",
  "история ведомостей",
  "журналы работы",
  "резервные копии",
];

const AdminCleanupPage: React.FC = () => {
  const [status, setStatus] = useState<SessionStatus>(emptyStatus);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<CleanupResult | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [analysis, setAnalysis] = useState<CleanupAnalysis | null>(null);
  const [preview, setPreview] = useState<ImportCleanupPreview | null>(null);
  const [isSafeDeleting, setIsSafeDeleting] = useState(false);
  const [examSessions, setExamSessions] = useState<ExamSession[]>([]);
  const [openedExamSession, setOpenedExamSession] = useState<ExamSession | null>(null);

  const authHeaders = {
    "X-User-Role": localStorage.getItem("role") || "",
    "X-Username": localStorage.getItem("username") || "",
  };

  const loadStatus = () => {
    setIsStatusLoading(true);

    fetch("/api/admin/session-status", {
      headers: authHeaders,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Не удалось загрузить состояние системы");
        }

        setStatus({
          ...emptyStatus,
          ...data,
          cleanupHistory: Array.isArray(data.cleanupHistory)
            ? data.cleanupHistory
            : [],
        });
      })
      .catch((error) => {
        setMessage(
          error instanceof Error
            ? error.message
            : "Не удалось загрузить состояние системы"
        );
      })
      .finally(() => setIsStatusLoading(false));
  };

  const loadAnalysis = () => {
    fetch("/api/admin/cleanup/students-analysis", {
      headers: authHeaders,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Не удалось загрузить анализ студентов");
        }

        setAnalysis(data);
      })
      .catch((error) => {
        setMessage(
          error instanceof Error ? error.message : "Не удалось загрузить анализ студентов"
        );
      });
  };

  const loadExamSessions = () => {
    fetch("/api/exam-sessions")
      .then(async (response) => {
        const data = await response.json().catch(() => []);

        if (!response.ok) {
          throw new Error(data.error || "Не удалось загрузить экзаменационные потоки");
        }

        setExamSessions(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        setMessage(
          error instanceof Error
            ? error.message
            : "Не удалось загрузить экзаменационные потоки"
        );
      });
  };

  useEffect(() => {
    loadStatus();
    loadAnalysis();
    loadExamSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenExamSession = async (sessionId: string) => {
    setMessage("");

    try {
      const response = await fetch(`/api/exam-sessions/${encodeURIComponent(sessionId)}`);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось открыть экзаменационный поток");
      }

      setOpenedExamSession(data);
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Не удалось открыть экзаменационный поток"
      );
    }
  };

  const handleOpenPreview = async (importId: string) => {
    setMessage("");

    try {
      const response = await fetch(`/api/admin/imports/${importId}/cleanup-preview`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось открыть анализ партии");
      }

      setPreview(data);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось открыть анализ партии");
    }
  };

  const handleSafeDeletePreview = async () => {
    if (!preview) return;
    if (
      !window.confirm(
        "Удалить только безопасные записи этой партии? Активные студенты и студенты с результатами удалены не будут."
      )
    ) {
      return;
    }

    setIsSafeDeleting(true);
    setMessage("");

    try {
      const response = await fetch(
        `/api/admin/imports/${preview.importId}/safe-students`,
        {
          method: "DELETE",
          headers: authHeaders,
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось выполнить безопасную очистку");
      }

      setMessage(
        `Безопасно удалено: ${data.deletedCount || 0}. Пропущено: ${
          data.skippedCount || 0
        }.`
      );
      setPreview(null);
      loadAnalysis();
      loadStatus();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Не удалось выполнить безопасную очистку"
      );
    } finally {
      setIsSafeDeleting(false);
    }
  };

  const handleCleanupSession = async () => {
    setIsLoading(true);
    setMessage("");
    setResult(null);

    try {
      const response = await fetch("/api/admin/cleanup-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось завершить сессию");
      }

      setResult({
        removedStudents: Number(data.removedStudents || 0),
        archiveName: String(data.archiveName || ""),
        archivePath: String(data.archivePath || ""),
      });
      setMessage("Экзаменационная сессия успешно завершена.");
      setStatus({
        ...emptyStatus,
        ...(data.status || {}),
        cleanupHistory: Array.isArray(data.status?.cleanupHistory)
          ? data.status.cleanupHistory
          : [],
      });
      setIsConfirmOpen(false);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось завершить сессию");
    } finally {
      setIsLoading(false);
    }
  };

  const stateItems = [
    { label: "Активных студентов", value: status.activeStudents },
    { label: "Администраторов", value: status.admins },
    { label: "Суперадминистраторов", value: status.superadmins },
    { label: "Результатов экзаменов", value: status.resultsCount },
    { label: "Протоколов", value: status.protocolsCount },
    { label: "Баз тестов", value: status.questionBanksCount },
    { label: "Баз ситуационных задач", value: status.taskBanksCount },
    {
      label: "Последняя резервная копия",
      value: status.lastBackup
        ? `${formatMoscowDate(status.lastBackup.createdAt)} ${formatMoscowTime(
            status.lastBackup.createdAt
          )}`
        : "—",
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <AdminNav section="cleanup" />

        <h2>Управление экзаменационной сессией</h2>

        <div className="admin-card">
          <h3>Текущее состояние системы</h3>
          {isStatusLoading && <p className="admin-muted">Загрузка...</p>}
          <div className="admin-summary-grid">
            {stateItems.map((item) => (
              <div className="admin-summary-card admin-summary-total" key={item.label}>
                <div className="admin-summary-value">{item.value}</div>
                <div className="admin-summary-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="admin-card" style={{ marginTop: 18 }}>
          <h3>Экзаменационные потоки</h3>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Дата создания</th>
                  <th>Название</th>
                  <th>Специальность</th>
                  <th>Назначение</th>
                  <th>Студентов</th>
                  <th>Завершили</th>
                  <th>Не завершили</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {examSessions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="admin-table-empty">
                      Потоки еще не созданы
                    </td>
                  </tr>
                ) : (
                  examSessions.map((session) => (
                    <tr key={session.sessionId}>
                      <td>{formatMoscowDate(session.createdAt)} {formatMoscowTime(session.createdAt)}</td>
                      <td>{session.name}</td>
                      <td>{session.specialty}</td>
                      <td>{session.assignmentType === "task" ? "Ситуационные задачи" : "Тестирование"}</td>
                      <td>{session.studentsCount}</td>
                      <td>{session.completedCount}</td>
                      <td>{session.incompleteCount}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-button admin-button-secondary"
                          onClick={() => handleOpenExamSession(session.sessionId)}
                        >
                          Открыть
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {openedExamSession && (
            <div className="admin-card" style={{ marginTop: 16 }}>
              <h3>{openedExamSession.name}</h3>
              <p className="admin-muted">
                Студентов: {openedExamSession.studentsCount}; завершили: {openedExamSession.completedCount}; не завершили: {openedExamSession.incompleteCount}
              </p>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ФИО</th>
                      <th>Логин</th>
                      <th>Специальность</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(openedExamSession.students || []).map((student) => (
                      <tr key={student.username}>
                        <td>{student.fullName || ""}</td>
                        <td>{student.username}</td>
                        <td>{student.specialty || ""}</td>
                        <td>{student.hasResult ? "Завершил" : "Не завершил"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          {preview && (
            <div className="admin-card" style={{ marginTop: 16 }}>
              <h3>Анализ партии: {preview.importFileName || preview.importId}</h3>
              <p>
                Всего: {preview.totalStudents}; активных: {preview.activeStudents}; с
                результатами: {preview.studentsWithResults}; назначены, не проходили:{" "}
                {preview.assignedWithoutResults}; безопасно удалить:{" "}
                {preview.safeDeleteCount}.
              </p>
              <button
                type="button"
                className="admin-button admin-button-danger"
                onClick={handleSafeDeletePreview}
                disabled={preview.safeDeleteCount === 0 || isSafeDeleting}
              >
                Удалить только безопасные записи
              </button>
            </div>
          )}
        <div className="admin-card" style={{ marginTop: 18 }}>
          <h3>Очистка студентов / Дубли</h3>
          {analysis?.summary ? (
            <>
              <div className="admin-summary-grid">
                <div className="admin-summary-card admin-summary-total">
                  <div className="admin-summary-value">{analysis.summary.totalStudentUsers}</div>
                  <div className="admin-summary-label">Всего студентов</div>
                </div>
                <div className="admin-summary-card admin-summary-passed">
                  <div className="admin-summary-value">{analysis.summary.activeStudents}</div>
                  <div className="admin-summary-label">Активных</div>
                </div>
                <div className="admin-summary-card admin-summary-total">
                  <div className="admin-summary-value">{analysis.summary.studentsWithResults}</div>
                  <div className="admin-summary-label">С результатами</div>
                </div>
                <div className="admin-summary-card admin-summary-incomplete">
                  <div className="admin-summary-value">{analysis.summary.assignedWithoutResults}</div>
                  <div className="admin-summary-label">Назначены, не проходили</div>
                </div>
                <div className="admin-summary-card admin-summary-failed">
                  <div className="admin-summary-value">
                    {analysis.summary.duplicateByFioBirthDateGroups}
                  </div>
                  <div className="admin-summary-label">Дублей по ФИО + дате</div>
                </div>
              </div>

              <p className="admin-muted">
                Активные студенты без результата считаются назначенными и не удаляются
                автоматически. Безопасная очистка удаляет только неактивные записи без
                результатов.
              </p>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Файл</th>
                      <th>Специальность</th>
                      <th>Назначение</th>
                      <th>Всего</th>
                      <th>Без результатов</th>
                      <th>С результатами</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analysis.batches || []).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="admin-table-empty">
                          Нет партий импорта
                        </td>
                      </tr>
                    ) : (
                      (analysis.batches || []).map((batch) => (
                        <tr key={batch.importId}>
                          <td>{formatMoscowDate(batch.importDate)}</td>
                          <td>{batch.importFileName || "—"}</td>
                          <td>{batch.specialty || "—"}</td>
                          <td>
                            {batch.assignmentType === "task"
                              ? "Ситуационные задачи"
                              : "Тестирование"}
                          </td>
                          <td>{batch.studentsCount}</td>
                          <td>{batch.studentsWithoutResultsCount}</td>
                          <td>{batch.studentsWithResultsCount}</td>
                          <td>
                            <button
                              type="button"
                              className="admin-button admin-button-secondary"
                              onClick={() => handleOpenPreview(batch.importId)}
                            >
                              Анализ партии
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="admin-muted">Анализ ещё не загружен.</p>
          )}
        </div>

        <div className="admin-card" style={{ marginTop: 18 }}>
          <h3>Что произойдет после завершения сессии</h3>
          <p>
            После завершения экзаменационной сессии будут удалены только активные
            пользователи студентов.
          </p>
          <p className="admin-muted">Будут сохранены:</p>
          <div style={{ display: "grid", gap: 8 }}>
            {preservedItems.map((item) => (
              <div key={item} style={{ color: "#166534", fontWeight: 600 }}>
                ✓ {item}
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card" style={{ marginTop: 18 }}>
          <button
            type="button"
            className="admin-button admin-button-danger"
            style={{ fontSize: 18, padding: "14px 18px" }}
            onClick={() => setIsConfirmOpen(true)}
            disabled={isLoading}
          >
            🗑 Завершить экзаменационную сессию
          </button>

          {message && <p className="admin-message">{message}</p>}

          {result && (
            <div className="admin-card" style={{ marginTop: 16 }}>
              <h3>Экзаменационная сессия успешно завершена.</h3>
              <p>Создан архив пользователей.</p>
              <p>Удалено студентов: {result.removedStudents}</p>
              <p>Архив сохранён:</p>
              <strong>{result.archivePath || result.archiveName}</strong>
            </div>
          )}
        </div>

        <div className="admin-card" style={{ marginTop: 18 }}>
          <h3>История завершения экзаменационных сессий</h3>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Время</th>
                  <th>Кто выполнил</th>
                  <th>Удалено студентов</th>
                  <th>Имя архива</th>
                </tr>
              </thead>
              <tbody>
                {status.cleanupHistory.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="admin-table-empty">
                      Завершений сессий ещё не было
                    </td>
                  </tr>
                ) : (
                  status.cleanupHistory.map((item) => (
                    <tr key={item.id}>
                      <td>{formatMoscowDate(item.createdAt)}</td>
                      <td>{formatMoscowTime(item.createdAt)}</td>
                      <td>{item.performedBy || "superadmin"}</td>
                      <td>{item.removedStudents}</td>
                      <td>{item.archiveName}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {isConfirmOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
              background: "rgba(15, 23, 42, 0.45)",
            }}
          >
            <div className="admin-card" style={{ maxWidth: 620, width: "100%" }}>
              <h3>Подтверждение завершения экзаменационной сессии</h3>
              <p>Будут удалены:</p>
              <ul>
                <li>все пользователи с ролями test</li>
                <li>все пользователи с ролями task</li>
              </ul>
              <p>Не будут удалены:</p>
              <ul>
                <li>admin</li>
                <li>superadmin</li>
                <li>результаты</li>
                <li>протоколы</li>
                <li>Р±Р°Р·С‹ тестов</li>
                <li>Р±Р°Р·С‹ задач</li>
                <li>история ведомостей</li>
                <li>архивы</li>
              </ul>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="admin-button"
                  onClick={() => setIsConfirmOpen(false)}
                  disabled={isLoading}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="admin-button admin-button-danger"
                  onClick={handleCleanupSession}
                  disabled={isLoading}
                >
                  {isLoading ? "Р—авершение..." : "Да, завершить сессию"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCleanupPage;
